import type { BuildSources } from "@brickme/project-core/src/image-build/create-bricked-image-from-source.ts";
import type {
	BrickedPictureOutput,
	Palette,
	PictureConfiguration,
} from "@brickme/project-core/src/model/picture.ts";
import type {
	BuildWorkerIncomingData,
	BuildWorkerOutgoingData,
} from "./build-worker-data.ts";

type BrickedBuildListener = (result: BrickedPictureOutput) => void;

function createBuildWorkerBridge() {
	// Note: neither format of worker works locally for me atm. Either in a built preview or
	// in dev mode. Haven't tried an isolated hello world. It's working fine on dev though.
	const worker = new Worker(new URL("./build-worker.ts", import.meta.url), {
		type: "module",
	});

	const listeners: Array<BrickedBuildListener> = [];

	function onMessage(e: MessageEvent) {
		const data = e.data as BuildWorkerOutgoingData;
		switch (data.type) {
			case "bricked-result": {
				for (const listener of listeners) {
					listener(data.output);
				}
				break;
			}
			default:
				console.error("Unknown build message", data);
				throw new Error("Editor got unknown message");
		}
	}
	worker.addEventListener("message", onMessage);

	return {
		addEventListener(_: "brickedBuild", listener: BrickedBuildListener) {
			listeners.push(listener);
		},
		removeEventListener(
			eventName: "brickedBuild",
			listener: BrickedBuildListener,
		) {
			const index = listeners.indexOf(listener);
			if (index === -1) {
				console.error(`Listener for ${eventName} not found`, listener);
				return;
			}

			listeners.splice(index, 1);
		},
		setBuildSources(sources: BuildSources | undefined) {
			worker.postMessage({
				type: "set-build-sources",
				sources,
			} satisfies BuildWorkerIncomingData);
		},
		clearPicture() {
			worker.postMessage({
				type: "clear-picture",
			} satisfies BuildWorkerIncomingData);
		},
		setPicture(picture: PictureConfiguration) {
			worker.postMessage({
				type: "set-picture",
				picture,
			} satisfies BuildWorkerIncomingData);
		},
		setPalette(palette: Palette | undefined) {
			worker.postMessage({
				type: "set-palette",
				palette,
			} satisfies BuildWorkerIncomingData);
		},
		terminate() {
			worker.terminate();
		},
	};
}

type BuildWorker = ReturnType<typeof createBuildWorkerBridge>;

export type { BuildWorker };
export { createBuildWorkerBridge };
